import { AutoHeight, Button, DialogV2, OutreachEditorRef } from '@recruit-robin/robin-theme/web-components';
import { ChevronLeft } from '@recruit-robin/robin-theme/web-icons';
import { AnimatePresence, motion } from 'motion/react';
import { useContext, useEffect, useMemo, useState } from 'react';

import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { PublicVacancyResponse } from 'clients/CampaignsClient/CampaignsClient.types';
import { VacancyDetails } from 'components/ui/organisms/VacancyDetails/VacancyDetails';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { useJobImprovedQuery } from 'shared/legacy/hooks/useJobImprovedQuery';
import { MessagePreview } from './MessagePreview/MessagePreview';
import { useStyles } from './PreviewAsCandidateModal.styles';

const ANIMATION_DURATION = 0.44;

export const PreviewAsCandidateModal = ({
  isVisible,
  onClose,
  messageEditorRef,
  selectedVacancy,
}: {
  isVisible: boolean;
  onClose: () => void;
  messageEditorRef: OutreachEditorRef | null;
  selectedVacancy: Vacancy;
}) => {
  const [step, setStep] = useState<'message' | 'vacancy'>('message');
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);

  const message = useMemo(() => {
    if (isVisible && messageEditorRef) {
      return messageEditorRef.serializeToHTML();
    }

    return '';
  }, [isVisible]);

  const handleBack = () => {
    if (step === 'vacancy') {
      setStep('message');
      return;
    }

    onClose();
  };

  useEffect(() => {
    if (!selectedVacancy.db_id) return;

    useJobImprovedQuery.prefetchQuery(selectedVacancy.db_id);
  }, [selectedVacancy.db_id]);

  return (
    <DialogV2.Root customCss={classes.dialog} isVisible={isVisible} variant="from-click">
      <DialogV2.Backdrop />
      <DialogV2.Content customCss={classes.content}>
        <DialogV2.Body customCss={classes.body}>
          <div css={classes.bodyHeader}>
            <Button
              label={dictionary.backToEditor}
              onClick={handleBack}
              style="text"
              type="subtle2"
              startIcon={ChevronLeft}
            />
          </div>

          <AutoHeight duration={ANIMATION_DURATION} ease="easeOut">
            <AnimatePresence mode="popLayout" initial={false}>
              {step === 'message' && (
                <motion.div
                  key="message"
                  css={classes.stepContainer}
                  initial={{ x: '-100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '-100%', opacity: 0 }}
                  transition={{ duration: ANIMATION_DURATION, ease: 'easeOut' }}>
                  <MessagePreview
                    message={message}
                    selectedVacancy={selectedVacancy}
                    onViewVacancy={() => setStep('vacancy')}
                  />
                </motion.div>
              )}
              {step === 'vacancy' && selectedVacancy.db_id && (
                <motion.div
                  key="vacancy"
                  css={classes.stepContainer}
                  initial={{ x: '100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: '100%', opacity: 0 }}
                  transition={{ duration: ANIMATION_DURATION, ease: 'easeOut' }}>
                  <_VacancyDetails jobId={selectedVacancy.db_id} />
                </motion.div>
              )}
            </AnimatePresence>
          </AutoHeight>
        </DialogV2.Body>
      </DialogV2.Content>
    </DialogV2.Root>
  );
};

const _VacancyDetails = ({ jobId }: { jobId: number }) => {
  const { data: currentUser } = useCurrentUserQuery();
  const { data: jobImproved } = useJobImprovedQuery(jobId);

  const publicVacancy = useMemo(() => {
    if (!jobImproved) return null;
    const tenantName = currentUser?.tenant?.name ? { tenant_name: currentUser?.tenant?.name } : {};

    return {
      job: { ...jobImproved, ...tenantName, db_id: jobId },
      accept_token: '',
      reject_token: '',
    } satisfies PublicVacancyResponse;
  }, [jobImproved, currentUser?.tenant?.name]);

  return <VacancyDetails vacancy={publicVacancy} isLoading={false} error={null} onSubmit={() => {}} readonly />;
};
